import API from './api';
import { handleApiError } from './utilsService';

// Servicio para los partidos
const matchService = {
  // Obtener todos los partidos
  getMatches: async (filters = {}) => {
    try {
      // Intentar obtener los partidos con un timeout más corto para la página de inicio
      const timeout = window.location.pathname === '/' ? 5000 : 30000;
      const response = await API.get('/matches', {
        params: filters,
        timeout: timeout,
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener los partidos:', error);

      // Si estamos en la página de inicio, devolver un array vacío en lugar de propagar el error
      if (window.location.pathname === '/') {
        console.warn('Error en la página de inicio, devolviendo array vacío');
        return [];
      }

      // Para otras páginas, propagar el error normalmente
      throw handleApiError(error, 'Error al obtener los partidos');
    }
  },

  // Obtener un partido por ID
  getMatchById: async (id) => {
    try {
      console.log('Obteniendo partido con ID:', id);
      const response = await API.get(`/matches/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener el partido:', error);
      throw handleApiError(error, 'Error al obtener el partido');
    }
  },

  // Crear un nuevo partido (solo admin y coach)
  createMatch: async (matchData) => {
    try {
      const response = await API.post('/matches', matchData);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al crear el partido');
    }
  },

  // Actualizar un partido (solo admin y coach)
  updateMatch: async (id, matchData) => {
    try {
      const response = await API.put(`/matches/${id}`, matchData);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al actualizar el partido');
    }
  },

  // Eliminar un partido (solo admin y coach)
  deleteMatch: async (id) => {
    try {
      const response = await API.delete(`/matches/${id}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al eliminar el partido');
    }
  },

  // Unirse a un partido
  joinMatch: async (id) => {
    try {
      console.log('Uniéndose al partido con ID:', id);
      const response = await API.post(`/matches/${id}/join`);
      return response.data;
    } catch (error) {
      console.error('Error al unirse al partido:', error);
      throw handleApiError(error, 'Error al unirse al partido');
    }
  },

  // Abandonar un partido
  leaveMatch: async (id) => {
    try {
      console.log('Abandonando el partido con ID:', id);
      const response = await API.post(`/matches/${id}/leave`);
      return response.data;
    } catch (error) {
      console.error('Error al abandonar el partido:', error);
      throw handleApiError(error, 'Error al abandonar el partido');
    }
  },

  // Actualizar estado de pago (solo admin y coach)
  updatePaymentStatus: async (id, userId, paymentStatus) => {
    try {
      const response = await API.put(`/matches/${id}/payment`, { userId, paymentStatus });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al actualizar el estado de pago');
    }
  },

  // Marcar asistencia (solo admin y coach)
  markAttendance: async (id, userId, attended) => {
    try {
      const response = await API.put(`/matches/${id}/attendance`, { userId, attended });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al marcar la asistencia');
    }
  },

  // Añadir momento destacado
  addHighlight: async (id, highlightData) => {
    try {
      const response = await API.post(`/matches/${id}/highlights`, highlightData);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al añadir el momento destacado');
    }
  },

  // Obtener partidos del usuario
  getUserMatches: async (filters = {}) => {
    try {
      // Verificar si hay un token de autenticación
      const token = localStorage.getItem('prokids_token');
      if (!token) {
        return []; // Si no hay token, devolver un array vacío
      }

      const response = await API.get('/matches/user', { params: filters });
      return response.data;
    } catch (error) {
      console.warn('Error al obtener los partidos del usuario:', error);
      return []; // En caso de error, devolver un array vacío
    }
  },

  // Duplicar un partido (solo admin y coach)
  duplicateMatch: async (id, newData = {}) => {
    try {
      const response = await API.post(`/matches/${id}/duplicate`, newData);
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al duplicar el partido');
    }
  },

  // Cancelar un partido (solo admin y coach)
  cancelMatch: async (id, reason) => {
    try {
      const response = await API.put(`/matches/${id}/cancel`, { reason });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al cancelar el partido');
    }
  },

  // Obtener estadísticas de partidos (solo admin)
  getMatchStats: async () => {
    try {
      const response = await API.get('/matches/stats');
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al obtener las estadísticas de partidos');
    }
  },

  // Obtener próximos partidos
  getUpcomingMatches: async () => {
    try {
      console.log('Obteniendo próximos partidos...');
      const response = await API.get('/matches', {
        params: { future: true, status: 'scheduled', limit: 5 },
      });
      console.log('Respuesta de próximos partidos:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error al obtener los próximos partidos:', error);
      throw handleApiError(error, 'Error al obtener los próximos partidos');
    }
  },

  // Subir imagen de un partido
  uploadMatchImage: async (id, imageFile) => {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await API.post(`/matches/${id}/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error, 'Error al subir la imagen del partido');
    }
  },

  // ===== MÉTODOS ADAPTADORES PARA COMPATIBILIDAD CON useCRUD =====

  // Método adaptador para obtener todos los partidos (compatible con useCRUD)
  getAll: async (filters = {}) => {
    try {
      console.log('matchService.getAll - Obteniendo todos los partidos con filtros:', filters);
      const response = await API.get('/matches', { params: filters });
      console.log('matchService.getAll - Respuesta recibida:', response.data);

      // Asegurar que siempre devolvemos un array
      const matches = Array.isArray(response.data) ? response.data : response.data?.matches || [];
      console.log('matchService.getAll - Array de partidos procesado:', matches);

      return matches;
    } catch (error) {
      console.error('matchService.getAll - Error:', error);
      throw handleApiError(error, 'Error al obtener los partidos');
    }
  },

  // Método adaptador para crear partido (compatible con useCRUD)
  create: async (matchData) => {
    try {
      console.log('matchService.create - Creando partido:', matchData);
      const response = await API.post('/matches', matchData);
      console.log('matchService.create - Partido creado:', response.data);
      return response.data;
    } catch (error) {
      console.error('matchService.create - Error:', error);
      throw handleApiError(error, 'Error al crear el partido');
    }
  },

  // Método adaptador para actualizar partido (compatible con useCRUD)
  update: async (id, matchData) => {
    try {
      console.log('matchService.update - Actualizando partido:', id, matchData);
      const response = await API.put(`/matches/${id}`, matchData);
      console.log('matchService.update - Partido actualizado:', response.data);
      return response.data;
    } catch (error) {
      console.error('matchService.update - Error:', error);
      throw handleApiError(error, 'Error al actualizar el partido');
    }
  },

  // Método adaptador para eliminar partido (compatible con useCRUD)
  delete: async (id) => {
    try {
      console.log('matchService.delete - Eliminando partido:', id);
      const response = await API.delete(`/matches/${id}`);
      console.log('matchService.delete - Partido eliminado:', response.data);
      return response.data;
    } catch (error) {
      console.error('matchService.delete - Error:', error);
      throw handleApiError(error, 'Error al eliminar el partido');
    }
  },

  // Método adaptador para obtener partido por ID (compatible con useCRUD)
  getById: async (id) => {
    try {
      console.log('matchService.getById - Obteniendo partido:', id);
      const response = await API.get(`/matches/${id}`);
      console.log('matchService.getById - Partido obtenido:', response.data);
      return response.data;
    } catch (error) {
      console.error('matchService.getById - Error:', error);
      throw handleApiError(error, 'Error al obtener el partido');
    }
  },
};

export default matchService;
